import Vue from 'vue'
import VueRouter from 'vue-router'
import Login from '../components/Login.vue'
import store from '../store'
import request from '@/utils/request'
const { canUseSkill } = require('@/utils/workbenchAccess.cjs')
const { PRODUCTION_SKILLS, isProductionWorker, canUseProduction, productionRouteAllowed } = require('@/utils/productionAccess.cjs')
const { loginLandingPath } = require('@/utils/sessionNavigation.cjs')
import { IOT_SYSTEM_DESIGN_NAME, SOLUTION_DESIGN_PATH, WORKBENCH_SKILLS } from '@/data/aimasonWorkbench'

const {
  isProjectManager: isProjectManagerUser,
  isSpaceAdmin: isSpaceAdminUser
} = require('@/utils/roomAccessUi.cjs')

Vue.use(VueRouter)

/**
 * 静态路由：不需要权限控制的页面
 */
const constantRoutes = [
  {
    path: '/',
    name: 'CommerceHome',
    component: () => import('@/views/commerce/Home.vue'),
    meta: { public: true, title: '产品首页' }
  },
  {
    path: '/workbench',
    name: 'AIMasonWorkbench',
    component: () => import('@/views/commerce/AIMasonWorkbench.vue'),
    meta: { public: true, title: 'AI工作室' }
  },
  {
    path: '/workbench/production/space',
    name: 'SpaceProductionWorkbench',
    component: () => import('@/views/production/ProductionWorkbench.vue'),
    meta: { requiresAuth: true, productionAccess: true, platformSuperOnly: true, workbenchSkill: 'space-intelligence-design', title: '空间智能设计师' }
  },
  {
    path: '/workbench/production/device',
    name: 'DeviceProductionWorkbench',
    component: () => import('@/views/production/ProductionWorkbench.vue'),
    meta: { requiresAuth: true, productionAccess: true, platformSuperOnly: true, workbenchSkill: 'device-intelligence-design', title: '设备智能设计师' }
  },
  {
    // 方案设计猫：下载桌面应用，并通过云平台一次性凭证启动。
    path: SOLUTION_DESIGN_PATH,
    name: 'SolutionDesignWorkbench',
    component: () => import('@/views/commerce/SolutionDesignWorkbench.vue'),
    meta: { requiresAuth: true, workbenchSkill: 'solution-design', platformSuperOnly: true, title: '方案设计' }
  },
  {
    path: '/workbench/cad',
    name: 'CadWorkbenchProjects',
    component: () => import('@/views/commerce/CadWorkbenchProjects.vue'),
    meta: { requiresAuth: true, title: IOT_SYSTEM_DESIGN_NAME, cadProjectAccess: true, workbenchSkill: 'iot-system-design' }
  },
  {
    path: '/workbench/cad/:projectId',
    name: 'CadWorkbenchProject',
    component: () => import('@/views/commerce/CadWorkbenchProject.vue'),
    meta: { requiresAuth: true, title: IOT_SYSTEM_DESIGN_NAME, cadProjectAccess: true, workbenchSkill: 'iot-system-design' }
  },
  {
    path: '/workbench/tasks',
    name: 'MissionTasks',
    component: () => import('@/views/mission/MissionTasks.vue'),
    meta: { requiresAuth: true, title: '我的AIMason任务', projectManagerOnly: true }
  },
  {
    path: '/workbench/tasks/:taskNo',
    name: 'MissionTask',
    component: () => import('@/views/mission/MissionTask.vue'),
    meta: { requiresAuth: true, title: 'AIMason任务详情', projectManagerOnly: true }
  },
  {
    path: '/workbench/admin',
    name: 'MissionAdminQueue',
    component: () => import('@/views/mission/MissionAdminQueue.vue'),
    meta: { requiresAuth: true, title: 'AIMason人工任务队列', projectManagerOnly: true }
  },
  {
    path: '/products/:code',
    name: 'CommerceProductDetail',
    redirect: to => ({ path: '/', query: { offer: to.params.code === 'DEDICATED_CLOUD_SERVER' || to.query.offer === 'DEDICATED_CLOUD_SERVER' ? 'DEDICATED_CLOUD_SERVER' : 'PROJECT_SPACE' } }),
    meta: { public: true, title: '产品详情' }
  },
  {
    path: '/buy/project',
    name: 'CommercePurchase',
    redirect: to => ({ path: '/', query: { offer: to.params.code === 'DEDICATED_CLOUD_SERVER' || to.query.offer === 'DEDICATED_CLOUD_SERVER' ? 'DEDICATED_CLOUD_SERVER' : 'PROJECT_SPACE' } }),
    meta: { public: true, title: '配置项目' }
  },
  {
    path: '/cloud/upgrade',
    name: 'CloudCapacityUpgrade',
    redirect: to => ({ path: '/', query: { offer: to.params.code === 'DEDICATED_CLOUD_SERVER' || to.query.offer === 'DEDICATED_CLOUD_SERVER' ? 'DEDICATED_CLOUD_SERVER' : 'PROJECT_SPACE' } }),
    meta: { public: true, title: '升级容量' }
  },
  {
    path: '/cloud/configurations',
    name: 'CloudConfigurations',
    redirect: '/',
    meta: { public: true, title: '我的配置' }
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/views/commerce/Register.vue'),
    meta: { public: true, title: '注册' }
  },
  { path: '/login', name: 'Login', component: Login, meta: { public: true, title: '登录' } },
  { path: '/404', name: 'NotFound', component: () => import('@/views/404.vue') },
  {
    // 监控控制台：只有平台超级管理员可进，复用物联云身份后跳到 /console/monitor/app/
    path: '/console/monitor',
    name: 'MonitorConsoleEntry',
    component: () => import('@/views/console/MonitorConsoleEntry.vue'),
    meta: { requiresAuth: true, platformSuperOnly: true, title: '监控控制台' }
  },
  {
    path: '/console',
    component: () => import('@/layout/index.vue'),
    redirect: '/console/projects',
    meta: { requiresAuth: true, projectManagerOnly: true },
    children: [
      {
        path: 'projects',
        name: 'CommerceProjectDrafts',
        component: () => import('@/views/commerce/ProjectDrafts.vue'),
        meta: { title: '我的项目申请', icon: 'el-icon-folder-opened', requiresAuth: true }
      }
    ]
  },
  {
    path: '/admin/kafka',
    name: 'KafkaMonitor',
    component: () => import('@/views/admin/KafkaMonitor.vue'),
    meta: { title: 'Kafka监控', requiresAuth: true, platformSuperOnly: true }
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('@/views/dashboard/index.vue'),
    meta: { title: '首页', icon: 'el-icon-s-home', requiresAuth: true, projectUserOnly: true }
  },
  {
    path: '/bigscreen',
    name: 'BigScreen',
    component: () => import('@/views/dashboard/BigScreen.vue'),
    meta: { title: '大屏可视化', icon: 'el-icon-monitor', requiresAuth: true, projectUserOnly: true }
  },
  {
    path: '/construction-dashboard',
    name: 'ConstructionDashboard',
    component: () => import('@/views/dashboard/ConstructionDashboard.vue'),
    meta: { title: '智慧工地', icon: 'el-icon-data-board', requiresAuth: true, projectUserOnly: true }
  },
  {
    path: '/energy',
    name: 'EnergyMonitor',
    component: () => import('@/views/energy/EnergyMonitor.vue'),
    meta: { title: '能耗监控', icon: 'el-icon-lightning', requiresAuth: true, projectUserOnly: true }
  },
  {
    path: '/building-energy',
    name: 'BuildingEnergyMonitor',
    component: () => import('@/views/energy/BuildingEnergyMonitor.vue'),
    meta: { title: '建筑能耗监控', icon: 'el-icon-office-building', requiresAuth: true, projectUserOnly: true }
  },
  {
    path: '/floor-energy',
    name: 'FloorEnergyMonitor',
    component: () => import('@/views/energy/FloorEnergyMonitor.vue'),
    meta: { title: '楼层能耗监控', icon: 'el-icon-s-data', requiresAuth: true, projectUserOnly: true }
  },
  {
    path: '/property-service',
    name: 'PropertyService',
    component: () => import('@/views/property/PropertyService.vue'),
    meta: { title: '物业服务', icon: 'el-icon-s-home', requiresAuth: true, projectUserOnly: true }
  },
  {
    path: '/leasing',
    name: 'Leasing',
    component: () => import('@/views/leasing/Leasing.vue'),
    meta: { title: '租售运营', icon: 'el-icon-house', requiresAuth: true, projectUserOnly: true }
  },
  {
    path: '/leasing/investment',
    name: 'LeasingInvestment',
    component: () => import('@/views/leasing/LeasingBusinessCenter.vue'),
    props: { domain: 'investment' },
    meta: { title: '招商管理', requiresAuth: true, projectUserOnly: true }
  },
  {
    path: '/leasing/lease-management',
    name: 'LeasingManagement',
    component: () => import('@/views/leasing/LeasingBusinessCenter.vue'),
    props: { domain: 'lease' },
    meta: { title: '租赁管理', requiresAuth: true, projectUserOnly: true }
  },
  {
    path: '/leasing/enterprise-services',
    name: 'LeasingEnterpriseServices',
    component: () => import('@/views/leasing/LeasingBusinessCenter.vue'),
    props: { domain: 'services' },
    meta: { title: '企业服务', requiresAuth: true, projectUserOnly: true }
  },
  {
    path: '/scheduler/rules',
    name: 'ScheduleRules',
    component: () => import('@/views/scheduler/ScheduleRules.vue'),
    meta: { title: '定时任务', icon: 'el-icon-time', requiresAuth: true, projectUserOnly: true }
  },
  {
    path: '/scheduler/calendars',
    name: 'HolidayCalendars',
    component: () => import('@/views/scheduler/HolidayCalendars.vue'),
    meta: { title: '假期日历', icon: 'el-icon-date', requiresAuth: true, projectManagerOnly: true }
  },
  {
    path: '/fire-safety',
    name: 'FireSafety',
    component: () => import('@/views/fire/FireSafety.vue'),
    meta: { title: '智慧消防', icon: 'el-icon-first-aid-kit', requiresAuth: true, projectUserOnly: true }
  },
  {
    path: '/security',
    name: 'SecurityMonitor',
    component: () => import('@/views/security/SecurityMonitor.vue'),
    meta: { title: '安防态势', icon: 'el-icon-lock', requiresAuth: true, projectUserOnly: true }
  },
  {
    path: '/video-monitor',
    name: 'VideoMonitor',
    component: () => import('@/views/security/VideoMonitor.vue'),
    meta: { title: '视频监控中心', icon: 'el-icon-video-camera', requiresAuth: true, projectUserOnly: true }
  },
  {
    path: '/access',
    name: 'AccessControlMonitor',
    component: () => import('@/views/access/AccessControlMonitor.vue'),
    meta: { title: '人车通行', icon: 'el-icon-truck', requiresAuth: true, projectUserOnly: true }
  },
  // 保留原有业务页面（需要登录但不走权限菜单）
  {
    path: '/building',
    name: 'BuildingViewer',
    component: () => import('../components/ProjectEntry.vue'),
    meta: { requiresAuth: true, projectUserOnly: true }
  },
  {
    path: '/test-platform',
    name: 'TestPlatformViewer',
    component: () => import('../components/BuildingViewer.vue'),
    meta: { requiresAuth: true, platformSuperOnly: true, title: '测试平台' }
  },
  {
    path: '/converter',
    name: 'DXFConverter',
    component: () => import('../components/DXFConverter.vue'),
    meta: { requiresAuth: true, projectManagerOnly: true }
  },
  {
    path: '/bim',
    name: 'BuildingManager',
    component: () => import('../components/BuildingManager.vue'),
    meta: { requiresAuth: true, projectManagerOnly: true }
  },
  {
    path: '/preview',
    name: 'ModelPreview',
    component: () => import('../components/ModelPreview.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/cameras',
    name: 'CameraManager',
    component: () => import('../components/CameraManager.vue'),
    props: () => ({ readOnly: !isProjectManagerUser(store.state.user || {}) }),
    meta: { requiresAuth: true, projectUserOnly: true }
  },
  {
    path: '/faces',
    name: 'FaceLibrary',
    component: () => import('../components/FaceLibrary.vue'),
    meta: { requiresAuth: true, platformSuperOnly: true }
  },
  {
    path: '/sites',
    name: 'SiteManager',
    component: () => import('../components/SiteManager.vue'),
    meta: { requiresAuth: true, projectManagerOnly: true }
  },
  {
    // 资源入口（3D 主屏 → 资源 → 场景库）：只读展示版，给客户看，仅 6 列
    path: '/scenes',
    name: 'SceneManagement',
    component: () => import('../components/SceneManagement.vue'),
    props: { readonly: true },
    meta: { requiresAuth: true, projectUserOnly: true }
  },
  {
    path: '/hardware-products',
    name: 'HardwareProducts',
    component: () => import('../components/HardwareProducts.vue'),
    meta: { title: '硬件产品', requiresAuth: true, projectManagerOnly: true }
  },
  {
    // 监控控制台顶栏「资源 → 系统状态」：从 3D 主屏弹窗抽出的独立页面
    path: '/system/status',
    name: 'SystemStatus',
    component: () => import('@/views/system/status/index.vue'),
    meta: { title: '系统状态', requiresAuth: true, projectManagerOnly: true }
  },
  {
    // 管理后台（3D 主屏 → 设置 → 平台管理）左侧菜单：完整场景库管理版，Layout 内嵌
    path: '/system/scene',
    component: () => import('@/layout/index.vue'),
    meta: { requiresAuth: true, projectManagerOnly: true },
    children: [
      {
        path: '',
        name: 'SystemSceneLibrary',
        component: () => import('@/views/iot/scene/index.vue'),
        meta: { title: '场景库', icon: 'el-icon-film', requiresAuth: true, projectManagerOnly: true }
      }
    ]
  },
  {
    // 产品管理(管理后台菜单)：产品目录 + HTML 说明书 + 扫码二维码
    path: '/system/product',
    component: () => import('@/layout/index.vue'),
    meta: { requiresAuth: true, projectManagerOnly: true },
    children: [
      {
        path: '',
        name: 'SystemProduct',
        component: () => import('@/views/system/product/index.vue'),
        meta: { title: '产品管理', icon: 'el-icon-goods', requiresAuth: true, projectManagerOnly: true }
      }
    ]
  },
  {
    // 北向集成(管理后台菜单)：第三方平台对接 — 接入方/推送通道/推送监控
    path: '/system/open',
    component: () => import('@/layout/index.vue'),
    meta: { requiresAuth: true, projectManagerOnly: true },
    children: [
      {
        path: '',
        name: 'SystemOpen',
        component: () => import('@/views/iot/open/index.vue'),
        meta: { title: '北向集成', icon: 'el-icon-share', requiresAuth: true, projectManagerOnly: true }
      }
    ]
  },
  {
    // 旧平台管理地址兼容：物联自动化能力已统一迁入 /iot 工作区。
    path: '/system/iot-business',
    component: () => import('@/layout/index.vue'),
    redirect: '/iot/point-strategies',
    meta: { title: '物联业务管理', requiresAuth: true, projectUserOnly: true },
    children: [
      {
        path: 'point-strategies',
        name: 'SystemPointStrategies',
        redirect: '/iot/point-strategies',
        meta: { title: '点位策略', icon: 'el-icon-coordinate', requiresAuth: true, projectManagerOnly: true }
      },
      {
        path: 'scenarios',
        name: 'SystemAutomationScenarios',
        redirect: '/iot/automation',
        meta: { title: '策略管理', icon: 'el-icon-s-operation', requiresAuth: true, projectUserOnly: true }
      },
      {
        path: 'schedules',
        name: 'SystemScheduleRules',
        redirect: '/iot/scheduler',
        meta: { title: '定时任务', icon: 'el-icon-time', requiresAuth: true, projectUserOnly: true }
      },
      {
        path: 'calendars',
        name: 'SystemHolidayCalendars',
        redirect: '/iot/calendars',
        meta: { title: '假期日历', icon: 'el-icon-date', requiresAuth: true, projectManagerOnly: true }
      },
      {
        path: 'strategy',
        name: 'SystemStrategyDashboard',
        redirect: '/iot/strategy',
        meta: { title: '策略引擎', icon: 'el-icon-magic-stick', requiresAuth: true, projectUserOnly: true }
      }
    ]
  },
  {
    path: '/zone-detail',
    name: 'ZoneDetailPage',
    component: () => import('../components/ZoneDetailPage.vue'),
    meta: { requiresAuth: true, projectUserOnly: true }
  },
  {
    path: '/space-history',
    name: 'SpaceHistoryDetail',
    component: () => import('@/views/space/HistoryDetail.vue'),
    meta: { title: '空间趋势历史', requiresAuth: true, projectUserOnly: true }
  },
  // 3D 主屏“策略管理”独立整页：新标签页打开，不套用后台 Layout。
  {
    path: '/strategy-management',
    name: 'StandaloneStrategyManagement',
    component: () => import('@/views/automation/AutomationScenarios.vue'),
    meta: { title: '策略管理', requiresAuth: true, projectManagerOnly: true }
  },
  // 自动化数据公式帮助独立页：由编辑窗口问号在新标签页中打开。
  {
    path: '/automation/formula-help',
    name: 'AutomationFormulaHelp',
    component: () => import('@/components/automation/AutomationFormulaHelpDialog.vue'),
    meta: { title: '自动化公式帮助', requiresAuth: true, projectUserOnly: true }
  },
  // 3D 主屏“实时告警”独立整页：不套后台侧边栏。
  {
    path: '/alerts',
    name: 'RealtimeAlerts',
    component: () => import('@/views/iot/alert/RealtimeAlerts.vue'),
    meta: { title: '实时告警', requiresAuth: true, projectUserOnly: true }
  },
  {
    path: '/project-logs',
    name: 'ProjectOperationLogs',
    component: () => import('@/views/iot/log/ProjectOperationLogs.vue'),
    meta: { title: '项目日志', requiresAuth: true, projectManagerOnly: true }
  },
  // 报警右键“工单”进入独立的填写与下发工作台，不在列表页内挤压表单。
  {
    path: '/workorder/create',
    name: 'WorkOrderCreate',
    component: () => import('@/views/iot/workorder/create.vue'),
    meta: { title: '填写并下发工单', requiresAuth: true, projectManagerOnly: true }
  },
  // ── AI 助手独立整页（侧边栏点击 → 新标签页打开此页，不套主布局）────────
  {
    path: '/assistant',
    name: 'AiAssistant',
    component: () => import('@/views/assistant/index.vue'),
    meta: { title: '平台智能助手', requiresAuth: true, projectUserOnly: true }
  },
  // ── 数据中心 DCIM（Layout 嵌套 + 独立大屏）演示 ──────────────────
  {
    path: '/dcim',
    component: () => import('@/layout/index.vue'),
    meta: { requiresAuth: true, projectUserOnly: true },
    children: [
      { path: 'overview', name: 'DcimOverview', component: () => import('@/views/dcim/Overview.vue'), meta: { title: 'DCIM 总览', requiresAuth: true } },
      { path: 'racks',    name: 'DcimRacks',    component: () => import('@/views/dcim/Racks.vue'),    meta: { title: '机柜 / U位', requiresAuth: true } },
      { path: 'env',      name: 'DcimEnv',      component: () => import('@/views/dcim/Env.vue'),      meta: { title: '动环监控', requiresAuth: true } },
      { path: 'pue',      name: 'DcimPue',      component: () => import('@/views/dcim/Pue.vue'),      meta: { title: 'PUE 能效', requiresAuth: true } },
      { path: 'assets',   name: 'DcimAssets',   component: () => import('@/views/dcim/Assets.vue'),   meta: { title: '资产 / 容量', requiresAuth: true } }
    ]
  },
  { path: '/dcim/screen', name: 'DcimScreen', component: () => import('@/views/dcim/Screen.vue'), meta: { requiresAuth: true, projectUserOnly: true } },
  { path: '/dcim/room3d', name: 'DcimRoom3D', component: () => import('@/views/dcim/Room3D.vue'), meta: { requiresAuth: true, projectUserOnly: true } },
  // ── IoT 功能模块（Layout 嵌套）───────────────────────────────────
  {
    path: '/iot',
    component: () => import('@/layout/index.vue'),
    meta: { requiresAuth: true, projectUserOnly: true },
    children: [
      {
        path: 'device',
        name: 'DeviceManage',
        component: () => import('@/views/iot/device/index.vue'),
        meta: { title: '设备管理', icon: 'el-icon-cpu', requiresAuth: true, projectUserOnly: true }
      },
      {
        path: 'grant-manage',
        name: 'GrantManage',
        component: () => import('@/views/iot/grant-manage/index.vue'),
        meta: { title: '扫码权限', icon: 'el-icon-key', requiresAuth: true, projectManagerOnly: true }
      },
      {
        path: 'qr-print',
        name: 'QrPrint',
        component: () => import('@/views/iot/qr-print/index.vue'),
        meta: { title: '设备二维码', icon: 'el-icon-picture-outline', requiresAuth: true, projectManagerOnly: true }
      },
      {
        path: 'template',
        name: 'DeviceTemplateManage',
        component: () => import('@/views/iot/template/index.vue'),
        meta: { title: '设备模板', icon: 'el-icon-files', requiresAuth: true, projectManagerOnly: true }
      },
      {
        path: 'data',
        name: 'DataMonitor',
        component: () => import('@/views/iot/data/index.vue'),
        meta: { title: '数据监控', icon: 'el-icon-data-line', requiresAuth: true, projectUserOnly: true }
      },
      {
        path: 'forecast',
        name: 'AiForecastCenter',
        component: () => import('@/views/iot/forecast/index.vue'),
        meta: { title: 'AI预测中心', icon: 'el-icon-magic-stick', requiresAuth: true, projectManagerOnly: true }
      },
      {
        path: 'board',
        name: 'DataBoardList',
        component: () => import('@/views/iot/board/index.vue'),
        meta: { title: '数据看板', icon: 'el-icon-data-analysis', requiresAuth: true, projectManagerOnly: true }
      },
      {
        path: 'board/:id',
        name: 'DataBoardDesign',
        component: () => import('@/views/iot/board/design.vue'),
        meta: { title: '看板详情', requiresAuth: true, projectManagerOnly: true }
      },
      {
        path: 'metric',
        name: 'MetricManage',
        component: () => import('@/views/iot/metric/index.vue'),
        meta: { title: '指标管理', icon: 'el-icon-odometer', requiresAuth: true, projectManagerOnly: true }
      },
      {
        path: 'point-strategies',
        name: 'PointStrategies',
        component: () => import('@/views/system/automation/PointStrategies.vue'),
        meta: { title: '点位策略', icon: 'el-icon-coordinate', requiresAuth: true, projectManagerOnly: true }
      },
      {
        path: 'automation',
        name: 'AutomationScenarios',
        component: () => import('@/views/automation/AutomationScenarios.vue'),
        meta: { title: '策略管理', icon: 'el-icon-s-operation', requiresAuth: true, projectUserOnly: true }
      },
      {
        path: 'scheduler',
        name: 'IotScheduleRules',
        component: () => import('@/views/scheduler/ScheduleRules.vue'),
        meta: { title: '定时任务', icon: 'el-icon-time', requiresAuth: true, projectUserOnly: true }
      },
      {
        path: 'calendars',
        name: 'IotHolidayCalendars',
        component: () => import('@/views/scheduler/HolidayCalendars.vue'),
        meta: { title: '假期日历', icon: 'el-icon-date', requiresAuth: true, projectManagerOnly: true }
      },
      {
        path: 'strategy',
        name: 'StrategyDashboard',
        component: () => import('@/views/strategy/StrategyDashboard.vue'),
        meta: { title: '策略引擎', icon: 'el-icon-magic-stick', requiresAuth: true, projectUserOnly: true }
      },
      {
        path: 'alert',
        name: 'AlertManage',
        component: () => import('@/views/iot/alert/index.vue'),
        meta: { title: '告警管理', icon: 'el-icon-warning', requiresAuth: true, projectUserOnly: true }
      },
      {
        path: 'workorder',
        name: 'WorkOrderManage',
        component: () => import('@/views/iot/workorder/index.vue'),
        meta: { title: '工单管理', icon: 'el-icon-tickets', requiresAuth: true, projectManagerOnly: true }
      },
      {
        path: 'asset',
        name: 'AssetManage',
        component: () => import('@/views/iot/asset/index.vue'),
        meta: { title: '设备台账', icon: 'el-icon-notebook-2', requiresAuth: true, projectUserOnly: true }
      },
      {
        path: 'commissioning',
        name: 'CommissioningManage',
        component: () => import('@/views/iot/commissioning/index.vue'),
        meta: { title: '安装核验', icon: 'el-icon-circle-check', requiresAuth: true, projectManagerOnly: true }
      },
      {
        // 已搬到管理后台(/system/open,3D 主屏 → 设置 → 管理),旧路径重定向兼容
        path: 'open',
        redirect: '/system/open'
      },
      {
        path: 'ota',
        name: 'OtaManage',
        component: () => import('@/views/iot/ota/index.vue'),
        meta: { title: 'OTA固件', icon: 'el-icon-upload2', requiresAuth: true, projectManagerOnly: true }
      },
      {
        path: 'ai',
        name: 'AiChat',
        component: () => import('@/views/iot/ai/index.vue'),
        // newWindow：侧边栏点击不在应用内跳转，而是 window.open 弹出独立窗口加载 /assistant
        meta: { title: 'AI助手', icon: 'el-icon-chat-dot-round', requiresAuth: true, newWindow: '/assistant', projectUserOnly: true }
      },
      {
        path: 'ddc/pending',
        name: 'DdcPending',
        component: () => import('@/views/iot/ddc/Pending.vue'),
        meta: { title: 'DDC待审核', icon: 'el-icon-time', requiresAuth: true, platformSuperOnly: true }
      },
      {
        path: 'ddc/:id',
        name: 'DdcDetail',
        component: () => import('@/views/iot/ddc/Detail.vue'),
        meta: { title: 'DDC详情', requiresAuth: true, hidden: true, projectManagerOnly: true }
      },
      // ── 停车管理（车辆通行）─────────────────────────────────────
      {
        path: 'parking',
        redirect: '/iot/parking/dashboard',
        meta: { title: '停车管理', icon: 'el-icon-truck', requiresAuth: true }
      },
      {
        path: 'parking/dashboard',
        name: 'ParkingDashboard',
        component: () => import('@/views/iot/parking/Dashboard.vue'),
        meta: { title: '数据概览', icon: 'el-icon-data-analysis', requiresAuth: true }
      },
      {
        path: 'parking/records',
        name: 'ParkingRecords',
        component: () => import('@/views/iot/parking/Records.vue'),
        meta: { title: '出入查询', icon: 'el-icon-document-copy', requiresAuth: true }
      },
      {
        path: 'parking/vehicles',
        name: 'ParkingVehicles',
        component: () => import('@/views/iot/parking/Vehicles.vue'),
        meta: { title: '车辆管理', icon: 'el-icon-truck', requiresAuth: true }
      },
      {
        path: 'parking/spaces',
        name: 'ParkingSpaces',
        component: () => import('@/views/iot/parking/Spaces.vue'),
        meta: { title: '车位管理', icon: 'el-icon-s-grid', requiresAuth: true }
      },
      {
        path: 'parking/monthly',
        name: 'ParkingMonthly',
        component: () => import('@/views/iot/parking/Monthly.vue'),
        meta: { title: '月卡管理', icon: 'el-icon-postcard', requiresAuth: true }
      },
      {
        path: 'parking/payments',
        name: 'ParkingPayments',
        component: () => import('@/views/iot/parking/Payments.vue'),
        meta: { title: '缴费查询', icon: 'el-icon-money', requiresAuth: true }
      },
      // ── 物业管理 ───────────────────────────────────────────────
      { path: 'property',           redirect: '/iot/property/dashboard',
        meta: { title: '物业管理', icon: 'el-icon-office-building', requiresAuth: true } },
      { path: 'property/dashboard', name: 'PropertyDashboard',
        component: () => import('@/views/iot/property/Dashboard.vue'),
        meta: { title: '物业概览', icon: 'el-icon-data-analysis', requiresAuth: true } },
      { path: 'property/owners',    name: 'PropertyOwners',
        component: () => import('@/views/iot/property/Owners.vue'),
        meta: { title: '业主管理', icon: 'el-icon-user-solid',  requiresAuth: true } },
      { path: 'property/units',     name: 'PropertyUnits',
        component: () => import('@/views/iot/property/Units.vue'),
        meta: { title: '房产管理', icon: 'el-icon-house',       requiresAuth: true } },
      { path: 'property/bills',     name: 'PropertyBills',
        component: () => import('@/views/iot/property/Bills.vue'),
        meta: { title: '物业账单', icon: 'el-icon-document',    requiresAuth: true } },
      { path: 'property/repairs',   name: 'PropertyRepairs',
        component: () => import('@/views/iot/property/Repairs.vue'),
        meta: { title: '报修工单', icon: 'el-icon-set-up',      requiresAuth: true } },

      // ── 能耗管理 ───────────────────────────────────────────────
      { path: 'energy',             redirect: '/iot/energy/dashboard',
        meta: { title: '能耗管理', icon: 'el-icon-lightning', requiresAuth: true } },
      { path: 'energy/dashboard',   name: 'EnergyDashboard',
        component: () => import('@/views/iot/energy/Dashboard.vue'),
        meta: { title: '能耗概览', icon: 'el-icon-data-analysis', requiresAuth: true } },
      { path: 'energy/electricity', name: 'EnergyElectricity',
        component: () => import('@/views/iot/energy/Electricity.vue'),
        meta: { title: '用电监控', icon: 'el-icon-lightning',     requiresAuth: true } },
      { path: 'energy/water',       name: 'EnergyWater',
        component: () => import('@/views/iot/energy/Water.vue'),
        meta: { title: '用水监控', icon: 'el-icon-coin',          requiresAuth: true } },
      { path: 'energy/reports',     name: 'EnergyReports',
        component: () => import('@/views/iot/energy/Reports.vue'),
        meta: { title: '能耗报表', icon: 'el-icon-document-copy', requiresAuth: true } },
      { path: 'energy/carbon',      redirect: '/iot/carbon/dashboard' },

      // ── 碳管理（独立业务域；复用能耗活动数据，不复用能耗运行边界）──
      { path: 'carbon',             redirect: '/iot/carbon/dashboard',
        meta: { title: '碳管理', icon: 'el-icon-data-analysis', requiresAuth: true } },
      { path: 'carbon/dashboard',   name: 'CarbonDashboard',
        component: () => import('@/views/iot/energy/Carbon.vue'),
        meta: { title: '碳排放分析', icon: 'el-icon-data-analysis', requiresAuth: true } },

      // ── 信息发布 ───────────────────────────────────────────────
      { path: 'info',           redirect: '/iot/info/notices',
        meta: { title: '信息发布', icon: 'el-icon-bell', requiresAuth: true } },
      { path: 'info/notices',   name: 'InfoNotices',
        component: () => import('@/views/iot/info/Notices.vue'),
        meta: { title: '通知公告', icon: 'el-icon-bell',     requiresAuth: true } },
      { path: 'info/screens',   name: 'InfoScreens',
        component: () => import('@/views/iot/info/Screens.vue'),
        meta: { title: '屏幕设备', icon: 'el-icon-monitor',  requiresAuth: true } },
      { path: 'info/materials', name: 'InfoMaterials',
        component: () => import('@/views/iot/info/Materials.vue'),
        meta: { title: '素材库',   icon: 'el-icon-picture',  requiresAuth: true } },
      { path: 'info/tasks',     name: 'InfoTasks',
        component: () => import('@/views/iot/info/Tasks.vue'),
        meta: { title: '发布任务', icon: 'el-icon-promotion', requiresAuth: true } }
    ]
  }
]

const router = new VueRouter({
  mode: 'history',
  routes: constantRoutes
})

// 是否已加载动态路由
let dynamicRoutesLoaded = false

router.beforeEach(async (to, from, next) => {
  const token = store.state.user.token
  const publicRoute = (to.matched || []).some(record => record.meta && record.meta.public)

  // meta.newWindow：点击此菜单不在应用内跳转，而是【新标签页打开整页】，当前页面保持不变。
  // 用于 AI 助手等独立页。仅拦截应用内点击(from.name 存在)，直接访问 URL 时正常进入。
  const nwRecord = (to.matched || []).find(r => r.meta && r.meta.newWindow)
  if (nwRecord && from.name && !isProductionWorker(store.state.user)) {
    try {
      const href = router.resolve(nwRecord.meta.newWindow).href
      window.open(href, '_blank')
    } catch (e) { /* ignore */ }
    return next(false)   // 取消当前跳转，停留在原页面
  }

  // 登录落点统一按角色决定，旧 redirect 参数不覆盖角色入口。
  if (to.path === '/login' || to.path === '/register') {
    if (token) {
      if (!store.state.user.profileLoaded) {
        try { await store.dispatch('user/refreshUserInfo') }
        catch (error) { /* 请求拦截器处理失效会话；网络错误不清除共享登录。 */ }
      }
      if (store.state.user.token) next(loginLandingPath(store.state.user))
      else next()
    } else {
      next()
    }
    return
  }

  // 官网商城页面和 preview 页面不需要登录
  if (publicRoute || to.path === '/preview') {
    if (token && !store.state.user.profileLoaded) {
      try { await store.dispatch('user/refreshUserInfo') }
      catch (error) { /* 401 由请求拦截器退出；临时网络错误保留共享登录。 */ }
    }
    if (isProductionWorker(store.state.user) && !productionRouteAllowed(to.path)) next('/workbench')
    else next()
    return
  }

  // 户型展示已改为独立静态页(可被 iframe 嵌入),旧链接重定向保活
  if (to.path === '/house-model') {
    window.location.replace('/house-viewer.html')
    return
  }

  // 未登录用户访问其他页面，跳转到登录页
  if (!token) {
    next({ path: '/login', query: { redirect: to.fullPath } })
    return
  }

  // 已登录但未加载动态路由
  if (!dynamicRoutesLoaded) {
    // 登录接口已经返回完整用户信息并生成过路由时直接放行；页面刷新后
    // profileLoaded 会恢复为 false，此时仍会重新校验服务端会话。
    if (store.state.user.profileLoaded) {
      dynamicRoutesLoaded = true
      next({ ...to, replace: true })
      return
    }
    try {
      await store.dispatch('user/refreshUserInfo')
      dynamicRoutesLoaded = true
      next({ ...to, replace: true })
    } catch (e) {
      store.dispatch('user/logout')
      next('/login')
    }
    return
  }

  const matchedMeta = (to.matched || []).map(record => record.meta || {})
  const userState = store.state.user || {}
  if (isProductionWorker(userState) && !productionRouteAllowed(to.path)) {
    next('/workbench')
    return
  }
  if (matchedMeta.some(meta => meta.productionAccess) && !canUseProduction(userState)) {
    next('/workbench')
    return
  }
  const platformSuper = Boolean(userState.userInfo && userState.userInfo.isSuperAdmin)
  const projectManager = isProjectManagerUser(userState)
  const projectUser = projectManager || isSpaceAdminUser(userState)
  if (matchedMeta.some(meta => meta.platformSuperOnly) && !platformSuper) {
    next('/404')
    return
  }
  if (matchedMeta.some(meta => meta.projectManagerOnly) && !projectManager) {
    next('/building')
    return
  }
  if (matchedMeta.some(meta => meta.cadProjectAccess) && !projectManager && !platformSuper) {
    next('/404')
    return
  }
  if (matchedMeta.some(meta => meta.projectUserOnly) && !projectUser) {
    next('/404')
    return
  }

  const workbenchMeta = matchedMeta.find(meta => meta.workbenchSkill)
  if (workbenchMeta) {
    const skill = WORKBENCH_SKILLS.find(item => item.id === workbenchMeta.workbenchSkill)
    if (!canUseSkill(userState, skill)) { next('/workbench'); return }
    const productionWorkbench = workbenchMeta.productionAccess && PRODUCTION_SKILLS.has(skill.id)
    if (!(platformSuper && workbenchMeta.cadProjectAccess) && !productionWorkbench) {
      try { await request.get('/auth/workbench/access', { params: { skillId: skill.id } }) }
      catch (error) { next('/workbench'); return }
    }
  }
  next()
})

// 登出时重置标记
export function resetDynamicRoutes() {
  dynamicRoutesLoaded = false
}

export default router
